/**
 * "Programar envio" — os atalhos do Gmail e a escolha livre de data e hora.
 *
 * OS ATALHOS SÃO O RECURSO, não enfeite. Quem programa quase sempre quer uma
 * de três coisas: amanhã cedo, amanhã à tarde, ou segunda de manhã. Obrigar
 * a preencher data e hora para o caso comum é o que faz ninguém usar.
 *
 * E CADA ATALHO MOSTRA O INSTANTE QUE VAI VALER ("29 de out., 08:00"). Sem
 * isso, "segunda-feira de manhã" é ambíguo numa sexta à noite — e a pessoa
 * só descobre qual segunda depois que a mensagem sai.
 */
import { useMemo, useState } from "react";

import { Icone } from "../ui/Icone";

/** O fuso do navegador, por extenso, como o Gmail mostra no cabeçalho. */
function nomeDoFuso(): string {
  try {
    const f = Intl.DateTimeFormat().resolvedOptions().timeZone;
    // "America/Sao_Paulo" não é o que alguém chama o próprio fuso. O nome
    // longo é, e vem do próprio navegador já traduzido.
    const longo = new Intl.DateTimeFormat("pt-BR", { timeZoneName: "long" })
      .formatToParts(new Date())
      .find((p) => p.type === "timeZoneName")?.value;
    return longo ?? f;
  } catch {
    return "";
  }
}

function as(hora: number, dias: number): Date {
  const d = new Date();
  d.setDate(d.getDate() + dias);
  d.setHours(hora, 0, 0, 0);
  return d;
}

/** Próxima segunda-feira. Hoje sendo segunda, é a da semana que vem. */
function proximaSegunda(hora: number): Date {
  const d = new Date();
  const falta = (8 - d.getDay()) % 7 || 7;
  d.setDate(d.getDate() + falta);
  d.setHours(hora, 0, 0, 0);
  return d;
}

const formatador = new Intl.DateTimeFormat("pt-BR", {
  day: "numeric",
  month: "short",
  hour: "2-digit",
  minute: "2-digit",
});

/** Para o `datetime-local`, que exige hora LOCAL sem fuso. */
function paraCampoLocal(d: Date): string {
  const p = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
}

export function ProgramarEnvio({
  aoEscolher,
  aoFechar,
}: {
  aoEscolher: (quando: Date) => void;
  aoFechar: () => void;
}) {
  const [livre, setLivre] = useState(false);
  const [valor, setValor] = useState(() => paraCampoLocal(as(8, 1)));

  const opcoes = useMemo(() => {
    const amanhaCedo = as(8, 1);
    const amanhaTarde = as(13, 1);
    const segunda = proximaSegunda(8);
    const lista = [
      { rotulo: "Amanhã de manhã", quando: amanhaCedo },
      { rotulo: "Amanhã à tarde", quando: amanhaTarde },
      { rotulo: "Segunda-feira de manhã", quando: segunda },
    ];
    // SE AMANHÃ JÁ É SEGUNDA, os dois primeiros atalhos já cobrem, e repetir
    // "segunda-feira de manhã" com a mesma data confunde em vez de ajudar.
    return lista.filter(
      (o, i) => i < 2 || o.quando.toDateString() !== amanhaCedo.toDateString(),
    );
  }, []);

  const fuso = nomeDoFuso();

  return (
    <div
      className="absolute bottom-full left-0 z-50 mb-2 w-80 rounded-lg border border-linha bg-white py-2 shadow-2xl"
      role="dialog"
      aria-label="Programar envio"
    >
      <div className="flex items-start justify-between px-4 pb-2">
        <div>
          <p className="text-sm font-medium text-tinta">Programar envio</p>
          {fuso && <p className="text-xs text-tinta-fraca">{fuso}</p>}
        </div>
        <button
          type="button"
          onClick={aoFechar}
          aria-label="Fechar"
          className="rounded p-1 text-tinta-fraca hover:bg-fundo hover:text-tinta"
        >
          <Icone nome="X" tamanho={16} />
        </button>
      </div>

      {!livre && (
        <>
          <ul className="border-t border-linha">
            {opcoes.map((o) => (
              <li key={o.rotulo}>
                <button
                  type="button"
                  onClick={() => aoEscolher(o.quando)}
                  className="flex w-full items-center justify-between px-4 py-2.5 text-left text-sm hover:bg-fundo"
                >
                  <span className="text-tinta">{o.rotulo}</span>
                  <span className="text-tinta-fraca">{formatador.format(o.quando)}</span>
                </button>
              </li>
            ))}
          </ul>
          <div className="border-t border-linha pt-1">
            <button
              type="button"
              onClick={() => setLivre(true)}
              className="flex w-full items-center gap-2 px-4 py-2.5 text-left text-sm text-tinta hover:bg-fundo"
            >
              <Icone nome="Calendar" tamanho={16} />
              Escolher data e hora
            </button>
          </div>
        </>
      )}

      {livre && (
        <div className="border-t border-linha px-4 pt-3">
          <label className="text-sm">
            <span className="text-tinta-fraca">Data e hora</span>
            <input
              type="datetime-local"
              value={valor}
              min={paraCampoLocal(new Date())}
              onChange={(e) => setValor(e.target.value)}
              className="mt-1 w-full rounded-lg border border-linha px-3 py-2 text-sm"
            />
          </label>
          <div className="mt-3 flex items-center gap-2 pb-1">
            <button
              type="button"
              onClick={() => {
                const d = new Date(valor);
                // `new Date("")` dá Invalid Date, e mandá-lo ao servidor
                // viraria 422 depois de a pessoa já ter clicado. Barra aqui.
                if (!Number.isNaN(d.getTime())) aoEscolher(d);
              }}
              className="rounded-lg bg-acao px-3 py-1.5 text-sm text-white hover:bg-acao-viva"
            >
              Programar
            </button>
            <button
              type="button"
              onClick={() => setLivre(false)}
              className="text-sm text-tinta-fraca hover:text-tinta"
            >
              Voltar
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
